// constants/referral.js
//
// Wire vocabulary for the member-referral surface:
//   GET /api/v1/referral-code   (mint-or-fetch my code + rewards + money stats)
//   GET /api/v1/referrals       (who I referred, newest first)
//
// The backend owns these values — this file MIRRORS them so the PWA can branch
// without magic strings. It is not a second source of truth. Verify against:
//   backend/app/Http/Controllers/Api/V1/Referrals/Concerns/ResolvesMemberReferralEligibility.php
//   backend/app/Enums/Referrals/MemberReferralRewardState.php
//   backend/app/Http/Requests/StoreRegistrationRequest.php  (the gate codes)

/**
 * The `error.code` values the two referral endpoints emit.
 *
 * NOTE the envelope: `{ "error": { "code": …, "reason"? : … } }` — a SINGULAR
 * object at the top level, NOT the JSON:API `errors[]` array the rest of this
 * repo reads. See the comment on extractReferralError() in useMemberReferral.js.
 */
export const REFERRAL_ERROR_CODE = Object.freeze({
  // 403 — shared by both 403 states; branch on `reason`, never on the code alone.
  NOT_ELIGIBLE: 'REFERRAL_NOT_ELIGIBLE',
  // 404 — no active member-audience gift program. `/referral-code` ONLY; the list
  // endpoint keeps serving history in this state, by design.
  PROGRAM_UNAVAILABLE: 'REFERRAL_PROGRAM_UNAVAILABLE',
  // 404 — global kill switch `poolit.referral.enabled` is off. Hits both endpoints.
  DISABLED: 'REFERRAL_DISABLED',
});

/**
 * The `error.reason` discriminator on a 403 REFERRAL_NOT_ELIGIBLE.
 *
 * `NOT_ACTIVATED` is the state EVERY brand-new member is in — a first-class
 * screen state, not an edge case. `SUPPLIER_MEMBERSHIP` is no longer emitted
 * since 2026-11-02: a shop gets its own view at /suppliers/referral/, and a
 * shop not yet approved gets `not_activated` like a seller. Kept for the
 * cleanup pass.
 */
export const REFERRAL_NOT_ELIGIBLE_REASON = Object.freeze({
  NOT_ACTIVATED: 'not_activated',
  SUPPLIER_MEMBERSHIP: 'supplier_membership',
});

/**
 * Codes this CLIENT synthesizes. They never appear on the wire — they exist so
 * a member-facing state that the API expresses some other way still resolves to
 * one i18n key through the same lookup.
 */
export const REFERRAL_CLIENT_ERROR_CODE = Object.freeze({
  // A 429 from the framework throttle (30/min). Carries no `error` envelope.
  RATE_LIMITED: 'REFERRAL_RATE_LIMITED',
  // `isActive: false` on an otherwise healthy 200 — see the note on REWARD_STATE
  // below and locked decision 14. Not an error response; a dead link in a live
  // payload.
  CODE_INACTIVE: 'REFERRAL_CODE_INACTIVE',
});

/**
 * The per-row badge on GET /api/v1/referrals.
 *
 * ⚠️ A DECLINED owner reward is reported as `UNDER_REVIEW`, deliberately and
 * irreversibly: a decline can still be overturned, so presenting it as a refusal
 * would tell a seller we refused to pay them when we may yet do so. Its cents
 * count in no money bucket. Do not add a "declined" member here.
 *
 * `NONE` is rare but real — a referral captured while the program's owner reward
 * was zero.
 */
export const REWARD_STATE = Object.freeze({
  NOT_DUE: 'not_due',
  UNDER_REVIEW: 'under_review',
  EARNED: 'earned',
  EXPIRED: 'expired',
  NONE: 'none',
});

/**
 * `error.code` on a 422 from POST /api/v1/registrations when the invite-only
 * gate rejects a signup. Present IF AND ONLY IF the invite is the SOLE reason
 * for the rejection, so `if (error.code)` can never swallow a field error.
 *
 * Consumed by P2-A. Two codes, two different recovery paths — never collapse
 * them into one message.
 */
export const REFERRAL_GATE_CODE = Object.freeze({
  // Arrived with no token at all. They must OBTAIN an invite link.
  REFERRAL_REQUIRED: 'REFERRAL_REQUIRED',
  // Had a token; it does not resolve. The link they hold is DEAD.
  REFERRAL_INVALID: 'REFERRAL_INVALID',
});

/**
 * The backend's hard row cap on GET /api/v1/referrals. `meta.total` carries the
 * true count and `meta.hasMore` flags the truncation.
 *
 * There is no cursor. Do NOT build a client-side workaround — locked decision 12
 * says render the cap, add one quiet line from `meta.total`, and report it so BE
 * learns whether it ever happens in the wild.
 */
export const REFERRAL_MAX_ROWS = 100;
